import { Edit2, Pencil } from "lucide-react";
import { workSetupOptions, EducationCertificateCard } from "./utils/utils";
import "./CareerJourneyFlow.css";

const formatSalary = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Math.round(Number(value));
  if (isNaN(num)) return value;
  if (num <= 9999) return num.toString();
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "’");
};

function CareerJourneyPage({
  readOnlyData,
  handleEditClick,
  onEditProfileClick,
  isClose,
  preFillData,
}) {
  const { common, roles } = readOnlyData;

  // Check if peerReview is active (handle number, string, or boolean)
  const peerReviewValue = common?.peerReview;
  const showPeerReviewBadge =
    peerReviewValue === 1 ||
    peerReviewValue === "1" ||
    peerReviewValue === true;

  return (
    <div className="main-content career-journey-view-container">
      <div className="container career-journey-view">
        <div className="page-header">
          <button
            className="back-link"
            onClick={isClose}
            style={{
              border: "none",
              background: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ width: "20px", height: "20px" }}
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to Schedules
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <h1 className="page-title">Career Journey </h1>
          </div>

          <div
            className="header-actions"
            style={{ display: "flex", gap: "1rem", alignItems: "center" }}
          >
            {onEditProfileClick && (
              <button
                type="button"
                className="cj-edit-profile-btn"
                onClick={onEditProfileClick}
                style={{ position: "static" }}
              >
                <Pencil size={18} /> Edit Profile
              </button>
            )}
            {handleEditClick && (
              <button
                className={`edit-btn-top-right ${showPeerReviewBadge ? "with-badge" : ""}`}
                onClick={handleEditClick}
                style={{ position: "static" }}
              >
                <Edit2 size={18} /> Edit Journey
              </button>
            )}
            {showPeerReviewBadge && (
              <div
                className="peer-review-badge-inline"
                title="This profile has been reviewed by one or more hiring managers who assessed the candidate’s experience, skills, and professional credibility."
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="20"
                  width="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M23 12l-2.44-2.79.34-3.7L17.3 4l-1.99-3-3.31 1.89L9.02 1 7.03 4l-3.6.51.34 3.7L1 12l2.77 2.53-.34 3.7 3.6.51L9.02 23l3-1.89L15.31 23l1.99-3 3.6-.51-.34-3.7zM10.09 16.36l-3.5-3.58 1.41-1.41 2.09 2.14 5.91-6L17.41 8z" />
                </svg>
                <span>Hiring Manager Evaluated</span>
              </div>
            )}
          </div>
        </div>

        {(common?.length === 0 || !common) &&
          (roles?.length === 0 || !roles) ? (
          <div className="loader-container">
            <div className="spinner"></div>
            <p>Loading career journey data...</p>
          </div>
        ) : (
          // <div className="cj-readonly-content card">
          <div className="cj-section-readonly">
            {/* Header: Career Journey (CJ) / EP */}
            <div className="cj-header-section">
              <h2 className="cj-main-title"> {common?.ep || "N/A"}</h2>
              <p className="cj-overview-text">{common?.overview}</p>
            </div>

            {/* Overall Experience */}
            {common?.overallExperience?.value > 0 &&
              common?.overallExperience?.unit && (
                <>
                  <hr className="cj-divider" />
                  <div className="cj-section-item">
                    <h3 className="cj-section-title">
                      Overall experience: {common.overallExperience.value}{" "}
                      {common.overallExperience.unit}
                    </h3>
                  </div>
                </>
              )}

            {/* Most Relevant Experiences */}
            {common?.relevantExperience &&
              common.relevantExperience.length > 0 && (
                <>
                  <hr className="cj-divider" />
                  <div className="cj-section-item">
                    <h3 className="cj-section-title">
                      Most relevant experiences:
                    </h3>
                    <ul className="cj-experience-list">
                      {common.relevantExperience.map((exp, i) => (
                        <li key={i} className="cj-experience-item">
                          {exp.role}, {exp.company}
                          {exp.duration && ` (${exp.duration})`}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

            {/* Main Achievements */}
            {common?.mainAchievements && (
              <>
                <hr className="cj-divider" />
                <div className="cj-section-item">
                  <h3 className="cj-section-title">Main Achievements:</h3>
                  <p className="cj-section-content">
                    {common.mainAchievements}
                  </p>
                </div>
              </>
            )}

            {/* Value Add */}
            {(common?.professional || common?.personal) && (
              <div className="cj-section-item">
                <h3 className="cj-section-title">Value add:</h3>
                {common?.professional && (
                  <p className="cj-section-content">
                    <strong>Professional:</strong> {common.professional}
                  </p>
                )}
                {common?.personal && (
                  <p className="cj-section-content">
                    <strong>Personal:</strong> {common.personal}
                  </p>
                )}
              </div>
            )}

            {/* The WHY */}
            {common?.why && (
              <div className="cj-section-item">
                <h3 className="cj-section-title">The WHY:</h3>
                <p className="cj-section-content">{common.why}</p>
              </div>
            )}

            {/* Key Details with Icons */}
            <hr className="cj-divider" />
            <div className="cj-key-details">
              <h3 className="cj-section-title">Key details:</h3>
              <div className="cj-detail-grid-v2">
                {common?.salaryExpectation > 0 && (
                  <div className="cj-detail-item-v2">
                    <span className="cj-detail-icon-v2">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="12" y1="1" x2="12" y2="23" />
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                      </svg>
                    </span>
                    <div className="cj-detail-content-v2">
                      <span className="cj-detail-label-v2">
                        Salary expectations:
                      </span>
                      <span className="cj-detail-text-v2">
                        {formatSalary(common.salaryExpectation)}{" "}
                        {common.salaryCurrency || "CHF"}{" "}
                        {common?.salaryPeriod &&
                          (common.salaryPeriod.startsWith("per")
                            ? common.salaryPeriod
                            : `per ${common.salaryPeriod}`)}
                      </span>
                    </div>
                  </div>
                )}

                {(common?.workSetup && common.workSetup.length > 0) ||
                  preFillData?.workSetup ? (
                  <div className="cj-detail-item-v2">
                    <span className="cj-detail-icon-v2">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect
                          x="3"
                          y="10"
                          width="18"
                          height="11"
                          rx="2"
                          ry="2"
                        />
                        <path d="M7 10V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v5" />
                      </svg>
                    </span>
                    <div className="cj-detail-content-v2">
                      <span className="cj-detail-label-v2">Work model:</span>
                      <span className="cj-detail-text-v2">
                        {(() => {
                          const setup =
                            common?.workSetup && common.workSetup.length > 0
                              ? common.workSetup
                              : preFillData?.workSetup || [];

                          if (!setup || setup.length === 0) return "N/A";

                          return setup
                            .map((item) => {
                              const match = workSetupOptions.find(
                                (opt) => opt.id == item || opt.name === item,
                              );
                              return match ? match.name : item;
                            })
                            .join(", ");
                        })()}
                      </span>
                    </div>
                  </div>
                ) : null}

                {((common?.languages && common.languages.length > 0) ||
                  (preFillData?.languages &&
                    preFillData.languages.length > 0)) && (
                    <div className="cj-detail-item-v2">
                      <span className="cj-detail-icon-v2">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <line x1="2" y1="12" x2="22" y2="12" />
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                        </svg>
                      </span>
                      <div className="cj-detail-content-v2">
                        <span className="cj-detail-label-v2">Languages:</span>
                        <span className="cj-detail-text-v2">
                          {common?.languages && common.languages.length > 0
                            ? common.languages
                              .map(
                                (l) =>
                                  `${l.name}${l.fluency ? ` (${l.fluency})` : ""
                                  }`,
                              )
                              .join(", ")
                            : preFillData.languages
                              ?.map(
                                (l) =>
                                  `${l.name}${l.fluency ? ` (${l.fluency})` : ""
                                  }`,
                              )
                              .join(", ")}
                        </span>
                      </div>
                    </div>
                  )}

                <div className="cj-detail-item-v2">
                  <span className="cj-detail-icon-v2">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </span>
                  <div className="cj-detail-content-v2">
                    <span className="cj-detail-label-v2">Notice period:</span>
                    <span className="cj-detail-text-v2">
                      {!common?.noticePeriod || common.noticePeriod.value === 0
                        ? "Available immediately"
                        : `${common.noticePeriod.value} ${common.noticePeriod.value === 1
                          ? common.noticePeriod.unit.replace(/s$/, "")
                          : common.noticePeriod.unit
                        }`}
                    </span>
                  </div>
                </div>

                {common?.location && common.location.length > 0 && (
                  <div className="cj-detail-item-v2">
                    <span className="cj-detail-icon-v2">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </span>
                    <div className="cj-detail-content-v2">
                      <span className="cj-detail-label-v2">Location:</span>
                      <span className="cj-detail-text-v2">
                        {common.location
                          .map((loc) => {
                            const cities =
                              loc.cities?.length > 0
                                ? loc.cities.join(", ")
                                : "";
                            return cities
                              ? `${loc.country} (${cities})`
                              : loc.country;
                          })
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Preferred Environment */}
            {common?.preferredEnvironment &&
              common.preferredEnvironment.length > 0 && (
                <>
                  <hr className="cj-divider" />
                  <div className="cj-section-item">
                    <h3 className="cj-section-title">
                      Preferred Company Type:
                    </h3>
                    <ul className="cj-experience-list">
                      {common.preferredEnvironment.map((env, i) => (
                        <li key={i} className="cj-experience-item">
                          {env}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

            {common?.preferredWorkEnvironment &&
              common.preferredWorkEnvironment.length > 0 && (
                <div className="cj-section-item">
                  <h3 className="cj-section-title">
                    Preferred Work Environment:
                  </h3>
                  <ul className="cj-experience-list">
                    {common.preferredWorkEnvironment.map((env, i) => (
                      <li key={i} className="cj-experience-item">
                        {env}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {/* Motivated by */}

            {/* Development potential is same as areas of improvements*/}
            {common?.developmentPotential && (
              <div className="cj-section-item">
                <h3 className="cj-section-title">Areas of Improvements:</h3>
                <p className="cj-section-content">
                  {common.developmentPotential}
                </p>
              </div>
            )}

            {/* ERP Experience */}
            {/* {common?.erpExperience && common.erpExperience.length > 0 && (
              <div className="cj-section-item">
                <h3 className="cj-section-title">ERP experience:</h3>
                <p className="cj-section-content">
                  {common.erpExperience
                    .map((e) =>
                      e.level ? `${e.system} (${e.level})` : e.system,
                    )
                    .join(", ")
                    .toLowerCase()}
                </p>
              </div>
            )} */}

            {/* Additional Systems */}
            {common?.additionalSystems &&
              common.additionalSystems.length > 0 && (
                <div className="cj-section-item">
                  <h3 className="cj-section-title">Additional systems:</h3>
                  <p className="cj-section-content">
                    {common.additionalSystems
                      .map((s) =>
                        s.level ? `${s.system} (${s.level})` : s.system,
                      )
                      .join(", ")
                      .toLowerCase()}
                  </p>
                </div>
              )}

            {/* Education and Certifications */}
            <div className="cj-section-inner">
              <EducationCertificateCard
                title="Education"
                items={common?.educations || []}
                type="education"
              />
            </div>

            <div className="cj-section-inner">
              <EducationCertificateCard
                title="Certifications"
                items={common?.certifications || []}
                type="certification"
              />
            </div>

            {/* Skills Section */}
            {((common?.skills && common.skills.length > 0) ||
              (preFillData?.skills && preFillData.skills.length > 0)) && (
                <div className="card-section">
                  <h3 className="card-title">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                      <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z" />
                    </svg>{" "}
                    Skills
                  </h3>
                  {(common?.skills && common.skills.length > 0
                    ? common.skills
                    : preFillData.skills
                  ).map((roleSkill, index) => (
                    <div key={index} className="card-item">
                      <h4 className="card-main">{roleSkill.job_role_name}</h4>
                      <ul className="cj-experience-list">
                        {roleSkill.skills.map((skillObj, i) => {
                          const skillName = Object.keys(skillObj)[0];
                          const skillData = skillObj[skillName];
                          return (
                            <li key={i} className="cj-experience-item">
                              <span style={{ fontWeight: "500" }}>
                                {skillName}
                              </span>
                              {skillData.level &&
                                (!skillData.subSkills ||
                                  skillData.subSkills.length === 0) &&
                                ` (${skillData.level})`}
                              {skillData.subSkills &&
                                skillData.subSkills.length > 0 && (
                                  <span
                                    className="cj-subskills"
                                    style={{
                                      color: "#666",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    :{" "}
                                    {skillData.subSkills
                                      .map((sub, idx) =>
                                        typeof sub === "string"
                                          ? sub
                                          : `${sub.name} (${sub.level})`,
                                      )
                                      .join(", ")}
                                  </span>
                                )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
          </div>
          // </div>
        )}
      </div>
    </div>
  );
}

export default CareerJourneyPage;
